import { RadioGroupProps, FormControl, RadioGroup, FormLabel, SxProps, Theme, Box, Typography } from '@mui/material';
import { FormControlLabel } from '@components/formControlLabel';
import { RadioButton } from './RadioButton';
import { adjustFontSize } from '@theme/styleOverrides/typography';
import { Tooltip } from '@components/tooltip';
import { InfoCircle } from '@assets/svgs';
import { FormHelperText } from '@components/index';

export type RadioItem = {
  value: string;
  label: string;
  secondaryLabel?: string;
  disabled?: boolean;
  sx?: SxProps | ((theme: Theme) => SxProps);
  tooltip?: string;
  helperText?: string;
};

export type RadioGroupFieldProps = RadioGroupProps & {
  inputLabel?: string;
  required?: boolean;
  disabled?: boolean;
  options: RadioItem[];
  sxProps?: {
    label?: SxProps;
  };
  layout?: 'horizontal' | 'vertical';
  hoverText?: string;
  helperText?: string;
};

export const RadioGroupField = ({
  onChange,
  options,
  required,
  inputLabel,
  disabled,
  defaultValue,
  sxProps = {},
  hoverText,
  layout = 'horizontal',
  helperText,
  ...other
}: RadioGroupFieldProps) => {
  return (
    <FormControl
      sx={{
        '& .MuiFormGroup-root': {
          margin: 0,
          marginLeft: '2px',
        },
        '& .Mui-focused': {
          color: ({ palette }) => `${palette.text.subdued} !important`,
        },
      }}
      required={required}
      disabled={disabled}
      fullWidth
    >
      {inputLabel && (
        <FormLabel
          sx={{
            color: 'text.caption',
            fontSize: (theme: Theme) => `${adjustFontSize(12, theme.palette.fontSize)}px`,

            '&.Mui-focused': {
              color: (theme: Theme) => `${theme.palette.text.caption} !important`,
            },
          }}
        >
          {inputLabel}
          {hoverText && (
            <Tooltip title={hoverText}>
              <Box sx={{ display: 'inline', color: 'text.subdued', position: 'relative', top: '4px' }}>
                {' '}
                <InfoCircle style={{ height: 15 }} />
              </Box>
            </Tooltip>
          )}
        </FormLabel>
      )}
      <RadioGroup row={layout === 'horizontal'} defaultValue={defaultValue} onChange={onChange} {...other}>
        {options.map((opt: RadioItem, index) => (
          <FormControlLabel
            key={opt.value}
            value={opt.value}
            tooltipTitle={opt.tooltip}
            control={
              <RadioButton
                disabled={disabled || opt.disabled}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.keyCode === 13) {
                    if (onChange) {
                      onChange({ target: { value: opt.value } } as any, opt.value);
                    }
                  }
                }}
              />
            }
            label={
              opt.secondaryLabel ? (
                <Box sx={{ display: 'inline' }}>
                  <Typography variant='bodySmall' fontWeight='bold' sx={{ color: 'secondary.main' }}>
                    {opt.label}
                    <Box component='span' fontWeight='normal' sx={{ color: 'text.primary' }}>
                      {' '}
                      ({opt.secondaryLabel})
                    </Box>
                  </Typography>
                </Box>
              ) : (
                opt.label
              )
            }
            sxProps={{
              formControlLabel: {
                marginRight: index !== options.length - 1 ? '4px !important' : '0px',
                ...sxProps?.label,
              } as any,
            }}
          />
        ))}
      </RadioGroup>
      <FormHelperText>{helperText}</FormHelperText>
    </FormControl>
  );
};
